import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import {
    //@ts-expect-error Striim-ui missing type declaration
    StriimBadge,
    StriimInputField,
    StriimButton,
    StriimDropdown,
    StriimCheckbox,
    StriimIconButton,
    StriimControlLabel,
    StriimTypography
} from "@striim/striim-ui";
import { Box, Grid, InputAdornment, SvgIcon } from "@mui/material";
import { Clear } from "@mui/icons-material";
import { styles } from "./select-tables-header.styles";
import { Close, Filter, Search, SpecialChevronDown } from "src/generic/icon/customIcons";
import InfoFilled from "app/images/striimline/info-fill.svg";
import utils from "core/utils";
import { Schema, Tables } from "../../schema-table";
import { matchTextWithWildcard } from "../../select-tables.utils";

interface SelectTablesHeaderProps {
    selectedCount: number;
    totalCount: number;
    entityType: string;
    searchInput?: string;
    setSearchInput?: Dispatch<SetStateAction<string>>;
    showSelectAll?: boolean;
    onSelectAll?: Function;
    selectedSchema: Schema;
    setFilteredTables: Dispatch<SetStateAction<Tables[] | null>>;
    currentListOfTables?: Tables[];
    onFilterApplied?: () => void;
    compact?: boolean;
}

interface FilterState {
    contains: string;
    doesNotContain: string;
    startsWith: string;
    endsWith: string;
    excludeTables: string;
    matchCase: boolean;
    isSelected: boolean;
}

const SelectTablesHeader: React.FC<SelectTablesHeaderProps> = ({
    selectedCount,
    totalCount,
    entityType,
    searchInput,
    setSearchInput,
    showSelectAll = true,
    onSelectAll,
    selectedSchema,
    setFilteredTables,
    currentListOfTables,
    onFilterApplied,
    compact = false
}) => {
    const controlState = useState<boolean>(false);
    const dropdownAnchorRef = useRef<HTMLDivElement>(null);

    const defaultFilters: FilterState = {
        contains: "",
        doesNotContain: "",
        startsWith: "",
        endsWith: "",
        excludeTables: "",
        matchCase: false,
        isSelected: false
    };

    const [filters, setFilters] = useState<FilterState>(defaultFilters);
    const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
    const [lastAppliedFilters, setLastAppliedFilters] = useState<FilterState>(defaultFilters);

    const areFiltersDifferent = (filters: FilterState, lastFilters: FilterState) =>
        Object.keys(filters).some(key => filters[key] !== lastFilters[key]);

    const hasFilters = areFiltersDifferent(filters, lastAppliedFilters);
    const isClearEnabled = areFiltersDifferent(filters, defaultFilters);

    const appliedFilterCount = [
        lastAppliedFilters.isSelected,
        lastAppliedFilters.matchCase,
        !!lastAppliedFilters.contains,
        !!lastAppliedFilters.doesNotContain,
        !!lastAppliedFilters.startsWith,
        !!lastAppliedFilters.endsWith,
        !!lastAppliedFilters.excludeTables
    ].filter(Boolean).length;

    const selectedTablesInSelectedSchema = currentListOfTables?.filter(table => !!table.isSelected)?.length || 0;
    const retainedTablesCount = selectedCount - selectedTablesInSelectedSchema;

    const matchText = (tableName: string, text: string, matchCase: boolean) => {
        return matchTextWithWildcard(tableName, text, matchCase);
    };

    const applyFiltersAndSearch = (searchTerm = searchInput, appliedFilters = filters) => {
        let filteredTablesList = [...selectedSchema.tables];
        setSearchInput(searchTerm);

        if (searchTerm?.length || appliedFilters.contains?.length) {
            filteredTablesList = filteredTablesList.filter(table =>
                matchText(table.name, searchTerm, appliedFilters.matchCase)
            );
        }
        if (appliedFilters.isSelected) {
            filteredTablesList = filteredTablesList.filter(table => table.isSelected);
        }
        if (appliedFilters.doesNotContain) {
            filteredTablesList = filteredTablesList.filter(
                table => !matchText(table.name, appliedFilters.doesNotContain, appliedFilters.matchCase)
            );
        }
        if (appliedFilters.startsWith) {
            filteredTablesList = filteredTablesList.filter(table =>
                appliedFilters.matchCase
                    ? table.name.startsWith(appliedFilters.startsWith)
                    : table.name.toLowerCase().startsWith(appliedFilters.startsWith.toLowerCase())
            );
        }
        if (appliedFilters.endsWith) {
            filteredTablesList = filteredTablesList.filter(table =>
                appliedFilters.matchCase
                    ? table.name.endsWith(appliedFilters.endsWith)
                    : table.name.toLowerCase().endsWith(appliedFilters.endsWith.toLowerCase())
            );
        }
        if (appliedFilters.excludeTables) {
            const excludeEntries = appliedFilters.excludeTables
                .split(";")
                .map(entry => {
                    // Quoted pattern: "schema"."table"
                    let match = entry.match(/"([^"]+)"\.?"([^"]+)"/);
                    if (match) {
                        return { schema: match[1].trim(), table: match[2].trim() };
                    }
                    // Unquoted pattern: schema.table
                    match = entry.match(/([^.;]+)\.([^.;]+)/);
                    return match ? { schema: match[1].trim(), table: match[2].trim() } : null;
                })
                .filter(Boolean);

            const matchSchema = (schema: string) =>
                appliedFilters.matchCase
                    ? schema === selectedSchema.name
                    : schema.toLowerCase() === selectedSchema.name.toLowerCase();

            if (excludeEntries.some(({ schema, table }) => matchSchema(schema) && table === "%")) {
                filteredTablesList = [];
            } else {
                const excludedTables = excludeEntries
                    .filter(({ schema }) => matchSchema(schema))
                    .map(({ table }) => (appliedFilters.matchCase ? table : table.toLowerCase()));

                filteredTablesList = filteredTablesList.filter(
                    table => !excludedTables.includes(appliedFilters.matchCase ? table.name : table.name.toLowerCase())
                );
            }
        }
        setFilteredTables(filteredTablesList);
        const hasActiveFilters = areFiltersDifferent(appliedFilters, defaultFilters);
        setFiltersApplied(hasActiveFilters);
        setLastAppliedFilters(appliedFilters);
        controlState[1](false);
    };

    const clearFilters = () => {
        setFilters(defaultFilters);
        setLastAppliedFilters(defaultFilters);
        applyFiltersAndSearch("", defaultFilters);
        setFiltersApplied(false);
        onFilterApplied?.();
    };

    const onCancel = () => {
        setFilters(lastAppliedFilters);
        controlState[1](false);
    };

    const clearSearch = () => {
        if (searchInput) {
            const updatedFilters = { ...lastAppliedFilters, contains: "" };
            setFilters(prev => ({ ...prev, contains: "" }));
            applyFiltersAndSearch("", updatedFilters);
        }
    };

    useEffect(() => {
        applyFiltersAndSearch();
    }, [selectedSchema]);

    return (
        <Grid container>
            <Grid container sx={compact ? styles.rootCompact : styles.root}>
                {showSelectAll && (
                    <Grid item sx={styles.rootChild} alignItems={"center"} gap={1}>
                        <StriimCheckbox
                            checked={!!selectedCount && selectedCount === totalCount}
                            indeterminate={!!selectedCount && selectedCount !== totalCount}
                            onChange={event => {
                                const value = event.target.checked;
                                onSelectAll && onSelectAll(value);
                            }}
                            data-test-id="select-all-checkbox"
                        />
                        <Box sx={styles.leftText}>
                            Select all ({selectedCount}/{totalCount} selected)
                        </Box>
                    </Grid>
                )}
                <Grid
                    item
                    ref={dropdownAnchorRef}
                    sx={{ ...styles.rootChild, position: "relative" }}
                    gap={compact ? 1 : 2}
                    flex={compact ? 1 : undefined}
                >
                    <StriimInputField
                        placeholder={`Search using ${utils.capitalize(entityType)} name`}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search opacity={0.6} fontSize={20} />
                                </InputAdornment>
                            ),
                            endAdornment: searchInput?.length ? (
                                <StriimIconButton variant="secondaryText" onClick={clearSearch} sx={styles.clearButton}>
                                    <Clear />
                                </StriimIconButton>
                            ) : null,
                            "data-test-id": "search-tables-input"
                        }}
                        value={searchInput}
                        onChange={value => {
                            const updatedFilters = { ...lastAppliedFilters, contains: value };
                            setFilters(prev => ({ ...prev, contains: value }));
                            setLastAppliedFilters(updatedFilters);
                            applyFiltersAndSearch(value, updatedFilters);
                        }}
                        sx={compact ? styles.searchInputCompact : styles.searchInput}
                    />
                    <StriimDropdown
                        controlState={controlState}
                        title={
                            <Grid container justifyContent={"space-between"} alignItems={"center"}>
                                <StriimTypography variant="h3" color={"primary.700"} data-test-id="open-tables-filter">
                                    Advanced Options
                                </StriimTypography>
                                <StriimIconButton onClick={() => controlState[1](false)}>
                                    <Close fontSize={20} />
                                </StriimIconButton>
                            </Grid>
                        }
                        extendOnClick
                        placement="bottom-end"
                        PopperProps={{
                            disablePortal: true,
                            anchorEl: dropdownAnchorRef.current,
                            sx: styles.advancedOptionsContainer
                        }}
                        PaperProps={{
                            sx: styles.advancedOptionsPaper
                        }}
                        content={
                            <Grid
                                container
                                flexDirection={"column"}
                                flexWrap={"nowrap"}
                                sx={{ ...styles.controlLabel, ...styles.advancedOptionsContent }}
                            >
                                <StriimControlLabel
                                    control={
                                        <StriimCheckbox
                                            checked={filters.isSelected}
                                            data-test-id="is-selected-filter"
                                            onChange={() =>
                                                setFilters(prev => ({ ...prev, isSelected: !prev.isSelected }))
                                            }
                                        />
                                    }
                                    label={
                                        <StriimTypography variant="body4" color={"greyscale.900"}>
                                            Is Selected
                                        </StriimTypography>
                                    }
                                />
                                <Grid container gap={2} p={2} flexDirection="column" flexWrap="nowrap">
                                    <StriimControlLabel
                                        control={
                                            <StriimCheckbox
                                                checked={filters.matchCase}
                                                data-test-id="match-case-filter"
                                                onChange={() =>
                                                    setFilters(prev => ({ ...prev, matchCase: !prev.matchCase }))
                                                }
                                            />
                                        }
                                        label={
                                            <StriimTypography variant="body4" color={"greyscale.900"}>
                                                Match Case
                                            </StriimTypography>
                                        }
                                    />
                                    <StriimInputField
                                        name="contains"
                                        label="Contains"
                                        value={filters.contains}
                                        data-test-id="contains-filter"
                                        onChange={value => {
                                            setFilters(prev => ({ ...prev, contains: value }));
                                        }}
                                    />
                                    <StriimInputField
                                        name="doesNotContain"
                                        label="Does not contain"
                                        data-test-id="not-contains-filter"
                                        value={filters.doesNotContain}
                                        onChange={value => setFilters(prev => ({ ...prev, doesNotContain: value }))}
                                    />
                                    <StriimInputField
                                        name="startsWith"
                                        label="Starts with"
                                        data-test-id="starts-filter"
                                        value={filters.startsWith}
                                        onChange={value => setFilters(prev => ({ ...prev, startsWith: value }))}
                                    />
                                    <StriimInputField
                                        name="endsWith"
                                        label="Ends with"
                                        data-test-id="ends-filter"
                                        value={filters.endsWith}
                                        onChange={value => setFilters(prev => ({ ...prev, endsWith: value }))}
                                    />
                                    <StriimInputField
                                        name="excludeTables"
                                        label="Exclude tables"
                                        data-test-id="exclude-filter"
                                        multiline
                                        rows={3}
                                        value={filters.excludeTables}
                                        onChange={value => setFilters(prev => ({ ...prev, excludeTables: value }))}
                                        helperText={`Enter tables in the format "<Schema>.<Table>", separated by semicolons or use wildcards("%").`}
                                        sx={{ whiteSpace: "pre-wrap" }}
                                    />
                                </Grid>
                                <Grid container gap={1} px={3} py={1} bgcolor={"greyscale.100"} flexWrap={"nowrap"}>
                                    <SvgIcon component={InfoFilled} sx={{ ...styles.infoIcon, mt: 0.25 }} />
                                    <StriimTypography variant="body4" color={"greyscale.700"}>
                                        Some selected {entityType.toLowerCase()}s may be hidden when filters are
                                        applied. Your selections are still retained.
                                    </StriimTypography>
                                </Grid>
                            </Grid>
                        }
                        bottomSection={
                            <Grid container justifyContent={"space-between"} py={1.25} px={1.5} sx={styles.borderTop}>
                                <Grid item>
                                    <StriimButton
                                        variant="text"
                                        onClick={clearFilters}
                                        disabled={!isClearEnabled}
                                        data-testid="clear-filters-button"
                                    >
                                        Clear All
                                    </StriimButton>
                                </Grid>
                                <Grid item display={"flex"} alignItems={"center"} gap={1}>
                                    <StriimButton
                                        variant="text"
                                        onClick={onCancel}
                                        disabled={!hasFilters}
                                        data-testid="cancel-filters-button"
                                    >
                                        Cancel
                                    </StriimButton>
                                    <StriimButton
                                        variant="secondary"
                                        onClick={() => {
                                            applyFiltersAndSearch(filters.contains);
                                            onFilterApplied?.();
                                        }}
                                        disabled={!hasFilters}
                                        data-testid="apply-filters-button"
                                    >
                                        Apply
                                    </StriimButton>
                                </Grid>
                            </Grid>
                        }
                    >
                        <StriimButton
                            variant="secondary"
                            style={{ background: "#fff" }}
                            startIcon={
                                <StriimBadge badgeContent={appliedFilterCount} invisible={appliedFilterCount === 0}>
                                    <SvgIcon component={Filter} />
                                </StriimBadge>
                            }
                        >
                            <SvgIcon component={SpecialChevronDown} sx={styles.chevronIcon} />
                        </StriimButton>
                    </StriimDropdown>
                </Grid>
            </Grid>
            {(searchInput?.length || filtersApplied) && retainedTablesCount > 0 && (
                <Grid
                    container
                    alignItems={"center"}
                    gap={1}
                    px={3}
                    py={1}
                    bgcolor={"greyscale.100"}
                    sx={styles.borderTop}
                >
                    <SvgIcon component={InfoFilled} sx={styles.infoIcon} />
                    <StriimTypography variant="body4" color={"greyscale.700"}>
                        Some selected{" "}
                        {retainedTablesCount === 1 ? entityType.toLowerCase() : `${entityType.toLowerCase()}s`}{" "}
                        {retainedTablesCount === 1 ? "is" : "are"} hidden in the current search or filters. Your
                        selections are still retained.
                    </StriimTypography>
                </Grid>
            )}
        </Grid>
    );
};

export default SelectTablesHeader;
